import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from './redux/store';
import { fetchProfile } from './redux/authSlice';

// Pages
import HomePage from './pages/HomePage';
import BuylistPage from './pages/BuylistPage';
import BuylistCartPage from './pages/BuylistCartPage';
import BuylistCheckoutPage from './pages/BuylistCheckoutPage';
import TermsPage from './pages/TermsPage';
import CollectionsPage from './pages/CollectionsPage';
import StandSalePage from './pages/StandSalePage';
import ContactPage from './pages/ContactPage';
import CsvUploadPage from './pages/CsvUploadPage';
import LoginPage from './pages/LoginPage';
import ShopifyCallbackPage from './pages/ShopifyCallbackPage';
import ProfilePage from './pages/ProfilePage';
import FaqPage from './pages/FaqPage';
import AboutPage from './pages/AboutPage';
import BlogPage from './pages/BlogPage';
import BlogPostPage from './pages/BlogPostPage';

// Global styles
import { createGlobalStyle } from 'styled-components';
import { theme, media } from './styles/theme';
import { brand, brandCssVariables } from './styles/brand';

const GlobalStyle = createGlobalStyle`
  /* Brand values as custom properties, so plain CSS can read the same source as the theme. */
  :root {
    ${brandCssVariables}
  }

  /* The header height changes with the logo, so anything sticking below it must follow. */
  @media (min-width: ${brand.header.breakpoint}) {
    :root {
      --tp-header-height: ${brand.header.heightDesktop};
    }
  }

  ${media.tablet} {
    :root {
      --tp-header-height: ${brand.header.heightSmall};
    }
  }

  * {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
  }

  html {
    scroll-behavior: smooth;
  }

  body {
    font-family: ${theme.fontFamily};
    /* The webshop's body scale. Set on body, not html, so rem-based spacing is unaffected. */
    font-size: ${brand.typography.bodyScale}rem;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    background-color: ${theme.colors.background};
    color: ${theme.colors.text};
  }

  h1, h2, h3, h4, h5, h6 {
    font-family: ${theme.fontFamilyHeading};
    font-weight: ${theme.fontWeights.medium};
    letter-spacing: ${brand.typography.headingLetterSpacing};
  }

  button, input, select, textarea {
    font-family: inherit;
  }

  .sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }
`;

const ScrollToTop: React.FC = () => {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
  }, []);

  useEffect(() => {
    if (hash) {
      const element = document.getElementById(hash.substring(1));
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
        return;
      }
    }
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [pathname, hash]);

  return null;
};

const AppContent: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { isAuthenticated } = useSelector((state: RootState) => state.auth);

  useEffect(() => {
    if (isAuthenticated) {
      dispatch(fetchProfile());
    }
  }, [dispatch, isAuthenticated]);

  return (
    <>
      <GlobalStyle />
      <ScrollToTop />
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/buylist" element={<BuylistPage />} />
        <Route path="/buylist-cart" element={<BuylistCartPage />} />
        <Route path="/buylist-checkout" element={<BuylistCheckoutPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/selge-samling" element={<CollectionsPage />} />
        <Route path="/collections" element={<Navigate to="/selge-samling" replace />} />
        <Route path="/selge-pa-stand" element={<StandSalePage />} />
        <Route path="/csv-opplasting" element={<CsvUploadPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/account/authorize" element={<ShopifyCallbackPage />} />
        <Route path="/profil" element={<ProfilePage />} />
        <Route path="/faq" element={<FaqPage />} />
        <Route path="/om-oss" element={<AboutPage />} />
        <Route path="/blogg" element={<BlogPage />} />
        <Route path="/blogg/:slug" element={<BlogPostPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
};

const App: React.FC = () => {
  return (
    <Router>
      <AppContent />
    </Router>
  );
};

export default App;
